import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { ICreateSessionEventOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import { SessionEventModelData } from "@/store/sessions/types";
import { uuidModule } from "@/modules/uuid";
import { JsonObject } from "type-fest";

export class CreateSessionEventOperation extends BaseSyncOperation<ICreateSessionEventOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_SESSION_EVENT";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const eventUpdate = this.generateCreateSessionEventUpdate();

    if (eventUpdate) optimisticUpdates.push(eventUpdate);

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.sessionEvents.recompute(this.payload.id);
  }

  protected generateCreateSessionEventUpdate(): OptimisticSyncUpdate<SessionEventModelData> {
    const optimisticUpdate: OptimisticSyncUpdate<SessionEventModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "SESSION_EVENT",
        model_version: 1,
        model_data: {
          id: this.payload.id,
          session_id: this.payload.session_id,
          // Threading refs render the reply in its thread immediately; the
          // server-confirmed row also carries the derived root id.
          session_thread_id: this.payload.session_thread_id ?? null,
          reply_to_session_event_id: this.payload.reply_to_session_event_id ?? null,
          kind: this.payload.event_kind,
          value_json: this.payload.value_json as JsonObject,
          event_value_json: this.payload.event_value_json as Record<string, never>,
          locally_created_at: new Date().toISOString(),
        },
        model_scopes: [],
      },
    };

    return optimisticUpdate;
  }
}
